import { Discount, DiscountComboDiscount } from '../../types/DiscountTypes'
import { Activity, Client, EnrollmentRegisteredPartner } from '../../types/GrooverTypes'
import { getActiveDiscounts } from '../DiscountHelper'
import { AdvancedDiscountLogic } from './AdvancedDiscountLogic'
import { CalculateDiscountLogic, Calculation, CalculationUserInput } from './CalculateDiscountTypes'
import { CombinedDiscountLogic } from './CombinedDiscountLogic'
import { CouplesDiscountLogic } from './CouplesDiscountLogic'
import { findBestMatchingComboRule, getComboPackageKey } from './ComboDiscountHelpers'
import { DiscountComboDiscountLogic } from './DiscountComboDiscountLogic'
import { DiscountCouponCodeLogic } from './DiscountCouponCodeLogic'
import { EarlyBirdDiscountLogic } from './EarlyBirdDiscountLogic'
import { MemberDiscountLogic } from './MemberDiscountLogic'
import { MultiCourseMatrixDiscountLogic } from './MultiCourseMatrixDiscountLogic'
import { ContinuationDiscountLogic } from './ContinuationDiscountLogic'
import { SeniorDiscountLogic } from './SeniorDiscountLogic'
import { SiblingDiscountLogic } from './SiblingDiscountLogic'
import { StudentDiscountLogic } from './StudentDiscountLogic'
import { ZeroDiscountLogic } from './ZeroDiscountLogic'

// we operate on price in cents, and want to have no decimals
export const fixMathRounding = (n: number): number => Math.round(n / 100) * 100

export const getBasePriceFromCalculation = (calculation: Calculation) => calculation.basePrice

export const getPriceFromCalculation = (calculation: Calculation) => {
  // if no discounts, return finalPrice (which may be quantity-multiplied for SingleEvent)
  if (calculation.discounts.length === 0) return calculation.finalPrice
  // else return last discount price
  return calculation.discounts[calculation.discounts.length - 1].output
}

export const getPriceFromCalculations = (calculations: Calculation[]) => {
  return calculations.reduce((acc, calculation) => {
    return fixMathRounding(acc + getPriceFromCalculation(calculation))
  }, 0)
}

export const createInitialCalculations = (activities: Activity[], userInput?: CalculationUserInput): Calculation[] => {
  // get initial calculations with no discounts
  return activities.map((activity) => {
    let basePrice = activity.price

    // For SingleEvent activities, multiply price by quantity if provided
    if (activity.eventType === 'SingleEvent' && userInput?.activityQuantities?.[activity.id]) {
      const quantity = userInput.activityQuantities[activity.id]
      basePrice = activity.price * quantity
    }

    return { activity, discounts: [], basePrice, finalPrice: basePrice }
  })
}

export const getActivitiesWithDiscountId = (calculations: Calculation[], discountId: string): string[] => {
  return calculations
    .map((calculation) => (calculation.activity.discountIds.includes(discountId) ? calculation.activity.id : null))
    .filter((activityId): activityId is string => activityId !== null)
}

export const getActivitiesForRegisteredPartners = (
  calculations: Calculation[],
  registeredPartners: EnrollmentRegisteredPartner[]
) => {
  return calculations.filter((calculation) =>
    registeredPartners.some((partner) => partner.activityId === calculation.activity.id)
  )
}

// returns class instance that implements given discount logic
export const discountLogicFactory = (discount: Discount): CalculateDiscountLogic => {
  if (discount.type === 'student') return new StudentDiscountLogic(discount)
  if (discount.type === 'sibling') return new SiblingDiscountLogic(discount)
  if (discount.type === 'senior') return new SeniorDiscountLogic(discount)
  if (discount.type === 'couples') return new CouplesDiscountLogic(discount)
  if (discount.type === 'combined') return new CombinedDiscountLogic(discount)
  if (discount.type === 'multi_course_matrix') return new MultiCourseMatrixDiscountLogic(discount)
  if (discount.type === 'continuation') return new ContinuationDiscountLogic(discount)
  if (discount.type === 'member') return new MemberDiscountLogic(discount)
  if (discount.type === 'advanced') return new AdvancedDiscountLogic(discount)
  if (discount.type === 'early_bird') return new EarlyBirdDiscountLogic(discount)
  if (discount.type === 'combo') return new DiscountComboDiscountLogic(discount)
  if (discount.type === 'code') return new DiscountCouponCodeLogic(discount)
  // Unsupported type or legacy discount type (e.g. 'subevent' or other from API/DB); treat as no-op.
  const discountType: string = (discount as { type: string }).type
  console.warn(`discountLogicFactory: discount type "${discountType}" is not implemented; will use ZeroDiscountLogic`)
  return new ZeroDiscountLogic()
}

const getBestDiscount = (activities: Activity[], client: Client, userInput: CalculationUserInput): Calculation[] => {
  const initial = createInitialCalculations(activities, userInput)

  const calculateSingleDiscount = (
    calculations: Calculation[],
    discount: Discount,
    userInput: CalculationUserInput
  ): Calculation[] => {
    const logic = discountLogicFactory(discount)
    let result = logic.calculate([...calculations], userInput)

    // Handle Enrollment scope for amount-based discounts
    if (discount.discountScope === 'Enrollment' && discount.calculationType === 'amount') {
      // Find the first activity that has this discount
      const firstActivityWithDiscount = result.find((calc) => calc.activity.discountIds.includes(discount.id))

      if (firstActivityWithDiscount) {
        // Apply discount only to the first activity
        result = result.map((calc) => {
          if (calc.activity.id === firstActivityWithDiscount.activity.id) {
            return calc
          }
          // Remove this discount from other activities
          calc.discounts = calc.discounts.filter((d) => d.id !== discount.id)
          calc.finalPrice =
            calc.discounts.length > 0 ? calc.discounts[calc.discounts.length - 1].output : calc.activity.price
          return calc
        })
      }
    }

    // Ensure no calculation results in negative price
    return result.map((calculation) => {
      if (calculation.discounts.length > 0) {
        // Get last discount
        const lastDiscount = calculation.discounts[calculation.discounts.length - 1]

        // If output would be negative, adjust the discount to make output 0
        if (lastDiscount.output < 0) {
          lastDiscount.output = 0
          lastDiscount.discount = lastDiscount.input // Discount is at most the input price
        }

        // Update finalPrice
        calculation.finalPrice = lastDiscount.output
      }
      return calculation
    })
  }

  // Fing best combinable discount
  const findBestValueDiscount = (discountGroup: Discount[], groupName?: string): Discount | null => {
    return (
      discountGroup.reduce((best: { discount: Discount; amount: number } | null, d) => {
        const calculationFreeze = JSON.parse(JSON.stringify(currentCalculations)) // freeze current calculations...
        const calcs = calculateSingleDiscount(calculationFreeze, d, userInput)

        return calcs.reduce((innerBest, activityCalc) => {
          const discountCalcs = groupName
            ? activityCalc.discounts.filter((dd) => dd.discountGroup === groupName)
            : activityCalc.discounts
          if (discountCalcs.length > 0) {
            const discountAmount = activityCalc.discounts.find((ddd) => ddd.id == d.id)?.discount || 0
            if (!innerBest || discountAmount > innerBest.amount) {
              return { discount: d, amount: discountAmount }
            }
          }
          return innerBest
        }, best)
      }, null)?.discount ?? null
    )
  }

  let currentCalculations: Calculation[] = [...initial]

  // Filter out deleted discounts
  const activeDiscounts = getActiveDiscounts(client.discounts)
  // Coupon codes stack after automatic discounts so they apply to the package/reduced price.
  // Each `type: 'code'` rule uses the same entered code; apply one definition to avoid doubling.
  const isCouponCodeDiscount = (d: Discount) => d.type === 'code'
  const couponCodeDiscounts = activeDiscounts.filter(isCouponCodeDiscount)

  // Prepare step 1: additional logic for combinable discounts
  const combinableDiscountsAll = activeDiscounts.filter((d) => d.canBeCombined && !isCouponCodeDiscount(d))
  const combinableDiscounts: Discount[] = []
  // Discount groups are opt-in via client.featureToggles.discountGroups (default off).
  const discountGroupsEnabled = client.featureToggles?.discountGroups === true

  // Apply each combinable discount in sequence
  const processedGroup = new Set<string>()
  combinableDiscountsAll.forEach((discount) => {
    // check if discount needs additional selection if has discountGroup
    if (discountGroupsEnabled && discount.discountGroup) {
      // skip group if already processed
      if (processedGroup.has(discount.discountGroup)) return

      // if this group is excluded, then...
      const discountGroup = combinableDiscountsAll.filter((d) => d.discountGroup === discount.discountGroup)
      if (discountGroup.some((d) => d.excludeGroupInCombination === discount.discountGroup)) {
        // ... find best discount in this group
        const bestDiscount = findBestValueDiscount(discountGroup, discount.discountGroup)

        if (bestDiscount) {
          processedGroup.add(discount.discountGroup)
          combinableDiscounts.push(bestDiscount)
          return
        }
      }
    }

    combinableDiscounts.push(discount)
  })

  // Step 1: Process combinable discounts
  combinableDiscounts.forEach((discount) => {
    currentCalculations = calculateSingleDiscount(currentCalculations, discount, userInput)
  })

  // Step 2: Process non-combinable discounts
  const nonCombinableDiscounts = activeDiscounts.filter((d) => !d.canBeCombined && !isCouponCodeDiscount(d))
  let bestNonCombinable = findBestValueDiscount(nonCombinableDiscounts)
  if (bestNonCombinable) {
    currentCalculations = calculateSingleDiscount(currentCalculations, bestNonCombinable, userInput)
  }

  // Step 3: coupon codes apply last (additive to combo/package and other automatic discounts)
  const winningComboRule = findBestMatchingComboRule(
    activeDiscounts.find((d): d is DiscountComboDiscount => d.type === 'combo')?.coursesDiscounts,
    activities.map((a) => a.id)
  )
  const codeBlockedByPackage =
    !!userInput.discountCode &&
    !!winningComboRule &&
    (userInput.discountCode.excludedPackageKeys ?? []).includes(getComboPackageKey(winningComboRule))
  if (couponCodeDiscounts.length > 0 && userInput.discountCode && !codeBlockedByPackage) {
    // A winning allowed package applies the code once to the package total.
    // Workshop exclusions only apply when the cart is bought as individual prices.
    const userInputForCode = winningComboRule
      ? {
          ...userInput,
          discountCode: { ...userInput.discountCode, excludedActivityIds: undefined }
        }
      : userInput
    currentCalculations = calculateSingleDiscount(currentCalculations, couponCodeDiscounts[0], userInputForCode)
  }

  // apply price overrides by setting new and only price discount
  if (userInput.priceOverride) {
    currentCalculations = currentCalculations.map((calculation) => {
      const override = userInput.priceOverride?.find((o) => o.activityId === calculation.activity.id)
      if (override) {
        calculation.discounts = [
          {
            type: 'price_override',
            name: override.label || 'Early Bird',
            id: override.reference,
            input: calculation.activity.price,
            discount: override.price,
            output: override.price,
            discountCalculationType: 'amount',
            discountValue: calculation.activity.price - override.price
          }
        ]
        calculation.finalPrice = override.price
      }
      return calculation
    })
  }

  return currentCalculations
}

export const calculateDiscounts = (
  activities: Activity[],
  client: Client,
  userInput: CalculationUserInput
): Calculation[] => {
  return getBestDiscount(activities, client, userInput)
}

export const getRegisteredPartnersFromState = (
  stateRegisteredPartners: Record<string, string>
): EnrollmentRegisteredPartner[] => {
  return Object.keys(stateRegisteredPartners || {}).map((activityId) => {
    return {
      activityId,
      enteredName: stateRegisteredPartners[activityId] || ''
    }
  })
}
